import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import translate from '../../../utilities/translator';
import { scrollToResults } from '../../../utilities/helpers';
import { differenceInDays, parse, intervalToDuration } from 'date-fns';
import { updateDatesFilter } from '../../mainFilter/mainFilterSlice';
import { clearSelectedHotel } from '../../search/searchSlice';
import { pushEvent } from '../../../utilities/analytics';
import { NIGHTS_FILTER_UPDATE_EVENT, SEC_FILTER_OPEN } from '../../../constants/analyticEvents';
import { selectFilterData } from '../../mainFilter/selectors';
import { selectSearch, updateSearch } from '../../mainFilter/searchSlice';
import { filterClearClick, filterSaveClick, setLastChangedFilter } from '../../../appSlice';
import NightButton from '../../../features/customize-button/components/buttons/NightButton';
import { clearPrefetchDeals } from '../../dealsList/prefetchDealsSlice';

export default function Nights(props) {
  const { side, isOpen, setIsOpen, addFilterToSet } = props;

  const { searchEngine } = useSelector(selectFilterData);
  const search = useSelector(selectSearch);
  const { nightsList } = searchEngine.when.initial;
  const selectedNights = searchEngine.when?.nights || [];
  const { when } = searchEngine;
  const { type, startDate, endDate } = when || {};
  const name = translate('holidayFinder.secondaryFilter.nights');

  const maxNights = when?.maxNights;
  const minNights = when?.minNights;
  const isSpecific = type === 'specific';
  const specificNights = differenceInDays(
    parse(endDate, 'dd/MM/yyyy', new Date()),
    parse(startDate, 'dd/MM/yyyy', new Date())
  );

  let daysInterval = 20;
  if (type === 'range' && startDate && endDate) {
    daysInterval = intervalToDuration({
      start: parse(startDate, 'dd/MM/yyyy', new Date()),
      end: parse(endDate, 'dd/MM/yyyy', new Date()),
    }).days;
  }

  const [nights, setNights] = useState(
    nightsList.map((night) => ({
      ...night,
      isSelected: minNights === night.value || maxNights === night.value,
    }))
  );

  const ref = useRef();
  const dispatch = useDispatch();

  const activeNights = nights.filter(({ isSelected }) => isSelected).map(({ value }) => value);

  useOnClickOutside(ref, () => isOpen && setIsOpen(false));

  const onSaveClick = (e) => {
    const minNights = isFinite(Math.min.apply(null, activeNights)) ? Math.min.apply(null, activeNights) : 0;
    const maxNights = isFinite(Math.max.apply(null, activeNights)) ? Math.max.apply(null, activeNights) : 0;

    dispatch(filterSaveClick());
    dispatch(clearPrefetchDeals());
    setIsOpen(false);
    const dates = {
      ...when,
      minNights: minNights !== 0 ? minNights : undefined,
      maxNights: maxNights !== 0 ? maxNights : undefined,
      nights: activeNights,
      type: type || 'month',
      endDate: isSpecific ? undefined : when.endDate,
      startDate: isSpecific ? undefined : when.startDate,
    };

    dispatch(updateDatesFilter(dates));
    // new
    dispatch(
      updateSearch({
        when: {
          ...search.when,
          minNights: dates.minNights,
          maxNights: dates.maxNights,
          nights: activeNights,
        },
      })
    );

    dispatch(clearSelectedHotel());
    dispatch(setLastChangedFilter('secondaryFilter'));
    scrollToResults();
    e.stopPropagation();
    pushEvent(NIGHTS_FILTER_UPDATE_EVENT, dates);
  };

  const onClearClick = (event) => {
    setIsOpen(false);
    dispatch(filterClearClick());
    const dates = {
      ...when,
      minNights: undefined,
      maxNights: undefined,
      nights: [],
      type: isSpecific ? 'month' : when.type,
      months: isSpecific ? [] : when.months,
      weekendsOnly: when.weekendsOnly,
      endDate: isSpecific ? undefined : when.endDate,
      startDate: isSpecific ? undefined : when.startDate,
    };
    dispatch(updateDatesFilter(dates));
    event?.stopPropagation();
    scrollToResults();
    pushEvent(NIGHTS_FILTER_UPDATE_EVENT, dates);
  };

  window.external_reset_nights = () => {
    addFilterToSet(() => {
      onClearClick();
    });
  };

  useEffect(() => {
    setNights((nights) =>
      when && when.nights
        ? nights.map((night) => ({
            ...night,
            isSelected: when.nights.includes(night.value),
          }))
        : nightsList
    );
  }, [maxNights, minNights, when]);

  const changeNights = (value) => () => {
    if (daysInterval && daysInterval >= value) {
      setNights(
        nights.map((night) => ({
          ...night,
          isSelected: value === night.value ? !night.isSelected : night.isSelected,
        }))
      );
    }
  };

  const openFilter = (st) => {
    if (!isOpen) {
      pushEvent(SEC_FILTER_OPEN, { filterName: 'nights' });
    }
    setIsOpen(st);
  };

  let activeName = searchEngine?.when?.nights?.join(', ') || '';

  activeName = `${activeName} ${translate('holidayFinder.secondaryFilter.nights_plural', { count: 2 })}`;

  const isActiveFilter = selectedNights.length > 0 || (isSpecific && specificNights);

  const getFilterLabel = () => {
    if (isSpecific) {
      if (specificNights) {
        return `${specificNights} ${translate('holidayFinder.secondaryFilter.nights_plural', {
          count: +specificNights,
        })}`;
      }

      // TODO: should be replaced by own translation key
      return `${translate('holidayFinder.mainFilter.any')} ${translate('holidayFinder.secondaryFilter.nights_plural', {
        count: 2,
      })}`;
    }

    return isActiveFilter ? activeName : name;
  };

  return (
    <div className="filter-item filter-item-v2" ref={ref}>
      <p
        className={`filter-item__name ${isOpen ? 'filter-item__name-open' : ''} ${
          isActiveFilter ? 'filter-item__name-active' : ''
        }`}
        onClick={() => !isSpecific && openFilter(!isOpen)}
        role="menuitem"
        ref={ref}
        tabIndex="0"
      >
        <span>{getFilterLabel()}</span>
        {isActiveFilter ? (
          <i onClick={(e) => onClearClick(e)} className="ri-close-line close" />
        ) : (
          !isSpecific && <i className="ri-arrow-down-s-line chevron" />
        )}
      </p>
      {isOpen && (
        <div className={`filter-body filter-body-v2 ${side}`} style={{ minWidth: "432px" }}>
          <div className="filter-nights__wrap">
            <div className="move-days-list">
              {nights.map(({ value, isSelected }) => (
                <NightButton
                  key={value}
                  value={value}
                  selected={isSelected}
                  onClick={changeNights(value)}
                  className={`filter-nights__item ${isSelected && 'filter-nights__item--selected'} ${
                    daysInterval && daysInterval < value ? 'not-active-night' : ''
                  }`}
                  notActive={(daysInterval && daysInterval < value)}
                />
              ))}
            </div>
          </div>
          <div className="filter-footer-v2 flex gap-3">
            <button className="panel-button panel-button__clear py-4 px-6 rounded-full" onClick={onClearClick}>
              {translate('holidayFinder.buttons.Clear')}
            </button>
            <button className="panel-button panel-button__save py-4 px-6 rounded-full" onClick={onSaveClick}>
              {translate('holidayFinder.buttons.Save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
